export interface Vital {
  _id: string;
  memberId: string;
  vitals: Array<{
    name: string;
    value: string;
    unit?: string;
  }>;
  createdAt: string;
  updatedAt: string;
}

export enum VitalName {
  HEIGHT = "height",
  WEIGHT = "weight",
  STERILIZATION = "sterilization",
}

export enum UnitType {
  KG = "kg",
  FT_IN = "ft/in",
  CM = "cm",
}

export interface VitalDto {
  vitals: Array<{
    name: string;
    value: string;
    unit?: string;
  }>;
}

export type SterilizationStatus = "yes" | "no" | "unsure";

export const sterilizationOptions: {
  value: SterilizationStatus;
  label: string;
}[] = [
  { value: "yes", label: "Yes, my pet is sterilized" },
  { value: "no", label: "No, not sterilized yet" },
  { value: "unsure", label: "I'm not sure" },
];

// Phase 48 — Vitals Tracking types (Human health)
// Transforms API DTOs (_id → id) for consistent client-side conventions

/**
 * VitalsParameter Enum
 * Three vital parameters supported by the vitals tracking system
 */
export enum VitalsParameter {
  BP = "BP",
  GLUCOSE = "GLUCOSE",
  WEIGHT = "WEIGHT",
  HEIGHT = "HEIGHT",
}

/**
 * VitalsSwitchMember
 * Member shape used by AddReadingModal's member-switch control. `name` is the
 * resolved display label for the switcher trigger; the remaining fields mirror
 * MemberPickerDrawer's expected shape (avatar/initials row) for the drawer list.
 */
export interface VitalsSwitchMember {
  id: string;
  name: string;
  first_name?: string;
  last_name?: string;
  nickname?: string;
  avatar?: string;
}

/**
 * BPReadingPayload
 * Blood pressure reading with systolic, diastolic, and optional pulse
 */
export interface BPReadingPayload {
  systolic: number;
  diastolic: number;
  pulse?: number;
}

/**
 * GlucoseReadingPayload
 * Blood glucose reading with value and optional category hint
 */
export interface GlucoseReadingPayload {
  value: number;
  category?: string;
}

/**
 * WeightReadingPayload
 * Body weight reading with optional height for BMI calculation
 */
export interface WeightReadingPayload {
  value: number;
  height?: number;
}

/**
 * RecordReadingDto
 * Request DTO for recording a new vital reading
 */
export interface RecordReadingDto {
  parameter: VitalsParameter;
  values: Record<string, any>;
  context?: string;
  note?: string;
  recordedAt?: Date;
  source?: string;
}

/**
 * UpdateReadingDto
 * Request DTO for updating an existing vital reading
 * Editable fields: values, context, note, recordedAt
 */
export interface UpdateReadingDto {
  values?: Record<string, any>;
  context?: string;
  note?: string;
  recordedAt?: Date;
}

/**
 * VitalsReading
 * Full vital reading record (transformed from API with _id → id)
 */
export interface VitalsReading {
  id: string;
  memberId: string;
  parameter: VitalsParameter;
  values: {
    systolic?: number;
    diastolic?: number;
    pulse?: number;
    value?: number;
    category?: string;
    height?: number;
    bmi?: number;
  };
  recordedAt: Date;
  context?: string;
  note?: string;
  /**
   * Reference range in force when this reading was saved. Classification uses
   * this rather than the member's current range — see getClassificationRange.
   */
  recordedRange?: VitalsRange;
  createdAt: Date;
  updatedAt?: Date;
}

/**
 * VitalsRange
 * Numeric range for a vital parameter (min/max values)
 * Used for BP systolic/diastolic and Glucose categories
 */
export interface VitalsRange {
  systolic?: [number, number];
  diastolic?: [number, number];
  fasting?: [number, number];
  postPrandial?: [number, number];
  random?: [number, number];
  beforeBed?: [number, number];
  height?: number; // stored in WEIGHT config; used to compute BMI client-side
}

/**
 * TrackingConfig
 * Member's vitals tracking configuration
 */
export interface TrackingConfig {
  id?: string;
  memberId: string;
  enabledParameters: VitalsParameter[];
  ranges: Partial<Record<VitalsParameter, VitalsRange>>;
  latestReadings?: Partial<Record<VitalsParameter, VitalsReading>>;
  confirmations?: unknown[];
  createdAt?: Date;
  updatedAt?: Date;
}

/**
 * UpdateRangeDto
 * Request DTO for updating vitals ranges
 */
export type UpdateRangeDto = Partial<Record<VitalsParameter, VitalsRange>>;

/**
 * ReadingConfirmation
 * Confirmation of a recorded initial reading with range snapshot
 */
export interface ReadingConfirmation {
  parameter: VitalsParameter;
  recordedAt: Date;
  values: Record<string, any>;
  recordedRange: Record<string, any>;
  recordingId: string;
  context?: string;
  note?: string;
}

/**
 * VitalsTrackingDto
 * Full vitals tracking state (enabled parameters, confirmations, ranges)
 * Represents the complete tracking configuration and recent readings
 */
export interface VitalsTrackingDto {
  enabledParameters: VitalsParameter[];
  confirmations: ReadingConfirmation[];
  ranges?: Record<string, any>;
}

// Backward compatibility aliases for Phase 46 code
export interface IEnableVitalsDto {
  enabledParameters: VitalsParameter[];
  initialReadings?: Record<string, any>;
}

export interface IDisableVitalsDto {
  parametersToDisable: VitalsParameter[];
}

export interface IReadingConfirmation {
  parameter: VitalsParameter;
  recordedAt: string;
  values: Record<string, any>;
  recordedRange: Record<string, any>;
  recordingId: string;
}

export interface IVitalsTrackingDto {
  enabledParameters: VitalsParameter[];
  confirmations: IReadingConfirmation[];
  ranges: Record<string, any>;
}

export interface IVitalsConfig {
  enabledParameters: VitalsParameter[];
  ranges: Record<string, any>;
}

/**
 * VitalsValueRanges
 * [min, max] per plotted series across the member's whole history for a
 * parameter, returned by the readings endpoint alongside the page.
 *
 * Charts seed their y-axis domain from this so the scale does not shift as
 * older pages load in. Keys: `systolic`/`diastolic` for BP, the reading
 * category for glucose, `value` for weight (kg) and height (cm). BMI is derived
 * client-side from height and has no server-side bounds.
 */
export type VitalsValueRanges = Record<string, [number, number]>;

/**
 * VitalsChartPoint
 * One x-axis slot of a vitals trend chart. Padding slots (rendered to keep a
 * short series right-aligned) carry no values and no `recordedAt`.
 */
export interface VitalsChartPoint {
  /** Unique per slot — the categorical x-axis key. */
  slot: string;
  /** Milliseconds since epoch; absent on padding slots. */
  time?: number;
  fullDate?: string;
  systolic?: number;
  diastolic?: number;
  value?: number;
  bmi?: number;
  pulse?: number;
  id?: string;
  sysOOR: boolean;
  diaOOR: boolean;
  valOOR: boolean;
}

/**
 * VitalsRangeStripProps
 * Single-reading presentation: one value plotted against its reference range
 * on a horizontal strip (shown until a second reading makes a trend possible).
 */
export interface VitalsRangeStripProps {
  label: string;
  value: number;
  unit: string;
  /** Series colour — also tints the reference band. */
  color: string;
  /** Scale the strip is drawn against; the value always sits strictly inside. */
  domain: [number, number];
  range?: [number, number];
  rangeLabel?: string;
  outOfRange: boolean;
}

export interface PrescriptionVitalSuggestion {
  prescriptionId: string;
  parameter: VitalsParameter;
  values: Record<string, any>;
  doctorName: string | null;
  dateOfVisit: string | null;
}
